import ActionDialog from "@dashboard/components/ActionDialog";
import DeleteFilterTabDialog from "@dashboard/components/DeleteFilterTabDialog";
import SaveFilterTabDialog from "@dashboard/components/SaveFilterTabDialog";
import {
  CategoryBulkDeleteMutation,
  CategoryDuplicateMutation,
  useCategoryBulkDeleteMutation,
  useCategoryDuplicateMutation,
  useRootCategoriesQuery,
} from "@dashboard/graphql";
import { useFilterPresets } from "@dashboard/hooks/useFilterPresets";
import useListSettings from "@dashboard/hooks/useListSettings";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { usePaginationReset } from "@dashboard/hooks/usePaginationReset";
import usePaginator, {
  createPaginationState,
  PaginatorContext,
} from "@dashboard/hooks/usePaginator";
import { useRowSelection } from "@dashboard/hooks/useRowSelection";
import { ListViews } from "@dashboard/types";
import createDialogActionHandlers from "@dashboard/utils/handlers/dialogActionHandlers";
import createSortHandler from "@dashboard/utils/handlers/sortHandler";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { getSortParams } from "@dashboard/utils/sort";
import { Box } from "@saleor/macaw-ui-next";
import isEqual from "lodash/isEqual";
import React, { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { CategoryListPage } from "../../components/CategoryListPage/CategoryListPage";
import {
  categoryListUrl,
  CategoryListUrlDialog,
  CategoryListUrlFilters,
  CategoryListUrlQueryParams,
} from "../../urls";
import { getActiveFilters, getFilterVariables, storageUtils } from "./filter";
import { getSortQueryVariables } from "./sort";

interface CategoryListProps {
  params: CategoryListUrlQueryParams;
}

export const CategoryList = ({ params }: CategoryListProps) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const { updateListSettings, settings } = useListSettings(ListViews.CATEGORY_LIST);
  const handleSort = createSortHandler(navigate, categoryListUrl, params);
  const {
    selectedRowIds,
    setSelectedRowIds,
    clearRowSelection,
    setClearDatagridRowSelectionCallback,
  } = useRowSelection(params);
  const {
    hasPresetsChanged,
    onPresetChange,
    onPresetDelete,
    onPresetSave,
    onPresetUpdate,
    getPresetNameToDelete,
    presets,
    selectedPreset,
    setPresetIdToDelete,
  } = useFilterPresets({
    params,
    storageUtils,
    getUrl: categoryListUrl,
    reset: clearRowSelection,
  });
  const [openModal, closeModal] = createDialogActionHandlers<
    CategoryListUrlDialog,
    CategoryListUrlQueryParams
  >(navigate, categoryListUrl, params);

  usePaginationReset(categoryListUrl, params, settings.rowNumber);

  const paginationState = createPaginationState(settings.rowNumber, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params),
      sort: getSortQueryVariables(params),
    }),
    [paginationState, params],
  );
  const { data, refetch } = useRootCategoriesQuery({
    displayLoader: true,
    variables: queryVariables,
  });
  const categories = mapEdgesToItems(data?.categories);
  const paginationValues = usePaginator({
    pageInfo: data?.categories?.pageInfo,
    paginationState,
    queryString: params,
  });
  const changeFilterField = (filter: CategoryListUrlFilters) => {
    clearRowSelection();
    navigate(
      categoryListUrl({
        ...getActiveFilters(params),
        ...filter,
        activeTab: !filter.query?.length ? undefined : params.activeTab,
      }),
    );
  };
  const handleCategoryBulkDeleteOnComplete = (data: CategoryBulkDeleteMutation) => {
    if (data?.categoryBulkDelete?.errors.length === 0) {
      navigate(categoryListUrl(), { replace: true });
      refetch();
      clearRowSelection();
    }
  };
  const handleSetSelectedCategoryIds = useCallback(
    (rows: number[], clearSelection: () => void) => {
      if (!categories) {
        return;
      }

      const rowsIds = rows.map(row => categories[row].id);
      const haveSaveValues = isEqual(rowsIds, selectedRowIds);

      if (!haveSaveValues) {
        setSelectedRowIds(rowsIds);
      }

      setClearDatagridRowSelectionCallback(clearSelection);
    },
    [categories, setClearDatagridRowSelectionCallback, selectedRowIds, setSelectedRowIds],
  );
  const [categoryBulkDelete, categoryBulkDeleteOpts] = useCategoryBulkDeleteMutation({
    onCompleted: handleCategoryBulkDeleteOnComplete,
  });
  const handleCategoryBulkDelete = useCallback(async () => {
    await categoryBulkDelete({
      variables: {
        ids: selectedRowIds,
      },
    });
    clearRowSelection();
  }, [selectedRowIds, categoryBulkDelete, clearRowSelection]);

  const generateUniqueName = useCallback((originalName: string, existingNames: string[]): string => {
    let counter = 1;
    let newName = `${originalName} ${counter}`;
    
    while (existingNames.includes(newName)) {
      counter++;
      newName = `${originalName} ${counter}`;
    }
    
    return newName;
  }, []);

  const generateUniqueSlug = useCallback((originalName: string): string => {
    // Create a base slug from the original name
    const baseSlug = originalName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    // Generate a random suffix to ensure uniqueness
    const randomSuffix = Math.random().toString(36).substring(2, 8); // 6 character random string
    const timestamp = Date.now().toString().slice(-4); // Last 4 digits of timestamp
    
    return `${baseSlug}-${randomSuffix}${timestamp}`;
  }, []);

  const [categoryDuplicate] = useCategoryDuplicateMutation({
    onCompleted: (data: CategoryDuplicateMutation) => {
      if (data?.categoryCreate?.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            id: 'FuLgMn',
            defaultMessage: "Categories duplicated successfully",
          }),
        });
        refetch();
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            id: '7vW115', 
            defaultMessage: "Failed to duplicate categories",
          }),
        });
      }
    },
  });

  const handleCategoryBulkDuplicate = useCallback(async () => {
    if (!categories) return;

    const categoriesToDuplicate = categories.filter(category => 
      selectedRowIds.includes(category.id)
    );
    
    const existingNames = categories.map(cat => cat.name);
    let successCount = 0;
    let failCount = 0;
    
    // Show progress notification
    notify({
      status: "info",
      text: intl.formatMessage({
        id: 'JvD1ZR',
        defaultMessage: 'Duplicating {count} categories...',
      }, { count: categoriesToDuplicate.length }),
    });
    
    // Execute all duplications in parallel for better performance
    const duplicatePromises = categoriesToDuplicate.map(async (category) => {
      try {
        const newName = generateUniqueName(category.name, existingNames);
        const newSlug = generateUniqueSlug(category.name);
        
        // Add to tracking arrays immediately to avoid conflicts
        existingNames.push(newName);
        
        await categoryDuplicate({
          variables: {
            input: {
              name: newName,
              slug: newSlug,
              description: null,
              seo: {
                description: null,
                title: null,
              },
            },
            parent: null,
          },
        });
        
        successCount++;

        return { success: true, category: category.name };
      } catch (error) {
        failCount++;

        return { success: false, category: category.name, error };
      }
    });
    
    // Wait for all duplications to complete
    try {
      await Promise.allSettled(duplicatePromises);
      
      // Show final result notification
      if (failCount === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            id: 'vCKe8X',
            defaultMessage: 'Successfully duplicated {count} categories',
          }, { count: successCount }),
        });
      } else if (successCount > 0) {
        notify({
          status: "warning",
          text: intl.formatMessage({
            id: 'WyIRyY',
            defaultMessage: 'Duplicated {successCount} categories, {failCount} failed',
          }, { successCount, failCount }),
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            id: '7vW115',
            defaultMessage: "Failed to duplicate categories",
          }),
        });
      }
      
      clearRowSelection();
      refetch(); // Refresh the list
    } catch (error) {
      notify({
        status: "error",
        text: intl.formatMessage({
          id: '7vW115',
          defaultMessage: "Failed to duplicate categories",
        }),
      });
    }
  }, [selectedRowIds, categories, categoryDuplicate, generateUniqueName, generateUniqueSlug, notify, intl, clearRowSelection, refetch]);

  return (
    <PaginatorContext.Provider value={paginationValues}>
      <CategoryListPage
        hasPresetsChanged={hasPresetsChanged()}
        categories={mapEdgesToItems(data?.categories)!}
        currentTab={selectedPreset}
        initialSearch={params.query || ""}
        onSearchChange={query => changeFilterField({ query })}
        onAll={() => navigate(categoryListUrl())}
        onTabChange={onPresetChange}
        onTabDelete={(tabIndex: number) => {
          setPresetIdToDelete(tabIndex);
          openModal("delete-search");
        }}
        onTabUpdate={onPresetUpdate}
        onTabSave={() => openModal("save-search")}
        tabs={presets.map(tab => tab.name)}
        settings={settings}
        sort={getSortParams(params)}
        onSort={handleSort}
        disabled={!data}
        onUpdateListSettings={(...props) => {
          clearRowSelection();
          updateListSettings(...props);
        }}
        selectedCategoriesIds={selectedRowIds}
        onSelectCategoriesIds={handleSetSelectedCategoryIds}
        onCategoriesDelete={() => openModal("delete")}
        onCategoriesDuplicate={handleCategoryBulkDuplicate}
      />

      <ActionDialog
        confirmButtonState={categoryBulkDeleteOpts.status}
        onClose={() =>
          navigate(
            categoryListUrl({
              ...params,
              action: undefined,
              ids: undefined,
            }),
          )
        }
        onConfirm={handleCategoryBulkDelete}
        open={params.action === "delete"}
        title={intl.formatMessage({
          id: "sG0w22",
          defaultMessage: "Delete categories",
          description: "dialog title",
        })}
        variant="delete"
      >
        <Box display="grid" gap={2}>
          <Box>
            <FormattedMessage
              id="Pp/7T7"
              defaultMessage="{counter,plural,one{Are you sure you want to delete this category?} other{Are you sure you want to delete {displayQuantity} categories?}}"
              values={{
                counter: params?.ids?.length,
                displayQuantity: <strong>{params?.ids?.length}</strong>,
              }}
            />
          </Box>
          <Box>
            <FormattedMessage
              id="e+L+q3"
              defaultMessage="Remember this will also delete all products assigned to this category."
            />
          </Box>
        </Box>
      </ActionDialog>

      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={onPresetSave}
      />

      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={onPresetDelete}
        tabName={getPresetNameToDelete()}
      />
    </PaginatorContext.Provider>
  );
};
export default CategoryList;
